import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

import Alert from "../../crmpostventa-shared/components/Alert";
import { LateTag, PriorityChip, StatusChip } from "../components/Chips";
import {
  CANAL_LABELS,
  ESTADO_LABELS,
  MOTIVO_CANCELACION_LABELS,
  codeOf,
  demoraTitle,
  formatCuit,
  formatDate,
  formatRelativeDays,
  formatRollos,
  formatShortDate,
  labelFor,
} from "../rolloFormat";
import {
  ACCIONES,
  LIMITS,
  MOTIVO_CANCELACION_OPTIONS,
  MOTIVO_NO_ENTREGA_OPTIONS,
  STEPS,
  accionLabel,
  cancelFieldId,
  describeTimelineEntry,
  errorIdOf,
  estadoChange,
  fieldId,
  isClosed,
  parseCantidad,
  timelineWhen,
} from "./gestionarModel";

// ---- Form field (design FormField): label with * or (opcional), control, then the error or the help text.
function Field({ id, label, required = false, optional = false, full = false, error, help, children }) {
  return (
    <div className={`crm-rollo__fld${full ? " crm-rollo__fld--full" : ""}${error ? " has-error" : ""}`}>
      <label className="crm-rollo__fld-label" htmlFor={id}>
        {label}
        {required ? (
          <span className="crm-rollo__req" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
        {optional ? <span className="crm-rollo__opt"> (opcional)</span> : null}
      </label>
      {children}
      {error ? (
        <span className="crm-rollo__err" id={errorIdOf(id)}>
          {error}
        </span>
      ) : help ? (
        <span className="crm-rollo__help">{help}</span>
      ) : null}
    </div>
  );
}

const invalidProps = (id, error) => (error ? { "aria-invalid": true, "aria-describedby": errorIdOf(id) } : {});

function ObservacionField({ id, value, error, required, placeholder, onChange }) {
  return (
    <Field id={id} label="Observación" required={required} optional={!required} full error={error}>
      <textarea
        className="crm-rollo__input crm-rollo__textarea"
        id={id}
        rows={3}
        placeholder={placeholder}
        value={value}
        aria-required={required || undefined}
        onChange={(event) => onChange(event.target.value)}
        {...invalidProps(id, error)}
      />
    </Field>
  );
}

function MotivoField({ id, options, value, error, onChange }) {
  return (
    <Field id={id} label="Motivo" required error={error}>
      <span className="crm-rollo__field crm-rollo__field--select">
        <select
          className="crm-rollo__select"
          id={id}
          value={value}
          aria-required="true"
          onChange={(event) => onChange(event.target.value)}
          {...invalidProps(id, error)}
        >
          <option value="">Elegí un motivo</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </span>
    </Field>
  );
}

// ---- Header data under the comercio name (design RolloManagePrep "Cabecera"). The server decides demorada;
// diasParaDemora (from the list response) only words the tag title.
export function GestionHeader({ gestion, diasParaDemora = null }) {
  const open = !isClosed(gestion?.estado);
  const dias = Number(gestion?.diasAbierta) || 0;
  const demorada = gestion?.demorada === true;
  const ubicacion = [
    gestion?.cuit ? `CUIT ${formatCuit(gestion.cuit)}` : "",
    gestion?.numeroTerminal ? `Terminal ${gestion.numeroTerminal}` : "Sin terminal",
    gestion?.provincia || "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <span className="crm-rollo__s">{ubicacion}</span>
      <dl className="crm-rollo__facts">
        <div>
          <dt>Estado</dt>
          <dd>
            <StatusChip estado={gestion?.estado} />
          </dd>
        </div>
        <div>
          <dt>Prioridad</dt>
          <dd>
            <PriorityChip prioridad={gestion?.prioridad} />
          </dd>
        </div>
        <div>
          <dt>Responsable</dt>
          <dd>{gestion?.responsableNombre || <span className="crm-rollo__muted">Sin asignar</span>}</dd>
        </div>
        <div>
          <dt>Antigüedad</dt>
          <dd>
            {formatDate(gestion?.createdAt)}
            {open ? (
              demorada ? (
                <LateTag title={demoraTitle(diasParaDemora)}>{formatRelativeDays(dias)}</LateTag>
              ) : (
                <span className="crm-rollo__muted">{formatRelativeDays(dias)}</span>
              )
            ) : null}
          </dd>
        </div>
        <div>
          <dt>Pedido</dt>
          <dd>
            {formatRollos(gestion?.cantidadSolicitada)}
            {gestion?.canalPedido ? ` · vía ${labelFor(CANAL_LABELS, gestion.canalPedido)}` : ""}
          </dd>
        </div>
      </dl>
    </>
  );
}

// ---- Stepper Solicitado → En preparación → En camino → Entregado; grey when the gestión was cancelled.
export function Stepper({ estado }) {
  const code = codeOf(estado);
  const cancelled = code === "CANCELADO";
  const current = STEPS.indexOf(code);

  return (
    <ol className={`crm-rollo__stepper${cancelled ? " is-off" : ""}`} aria-label="Estados de la gestión">
      {STEPS.map((step, index) => {
        let state = "";
        if (!cancelled && (index < current || (code === "ENTREGADO" && index === current))) state = "is-done";
        else if (!cancelled && index === current) state = "is-current";
        return (
          <li key={step} className={state} aria-current={state === "is-current" ? "step" : undefined}>
            <span className="crm-rollo__step-dot" aria-hidden="true">
              {state === "is-done" ? <FontAwesomeIcon icon={faCheck} /> : index + 1}
            </span>
            <span>{ESTADO_LABELS[step]}</span>
            {state === "is-current" ? <span className="crm-rollo__step-now">Actual</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

// ---- Timeline (design Timeline): newest first, as the server sends it. Entries added since the last load are
// highlighted.
export function Timeline({ entries, gestion, freshIds = [] }) {
  const items = entries || [];
  return (
    <>
      <h3>Línea de tiempo</h3>
      {items.length ? (
        <ol className="crm-rollo__tl">
          {items.map((entry, index) => {
            const change = estadoChange(entry);
            const detail = describeTimelineEntry(entry, gestion);
            const classes = [index === 0 ? "is-latest" : "", freshIds.includes(entry.id) ? "is-fresh" : ""].filter(Boolean);
            return (
              <li key={entry.id ?? index} className={classes.join(" ") || undefined}>
                <div className="crm-rollo__tl-body">
                  <div className="crm-rollo__tl-when">{timelineWhen(entry)}</div>
                  <div className="crm-rollo__tl-what">
                    <b>{accionLabel(entry.accion)}</b>
                    {change ? (
                      <span className="crm-rollo__tl-change">
                        {change.from ? (
                          <>
                            <StatusChip estado={change.from} />
                            <span className="crm-rollo__tl-arrow" aria-label="pasa a">
                              →
                            </span>
                          </>
                        ) : null}
                        <StatusChip estado={change.to} />
                      </span>
                    ) : null}
                  </div>
                  {detail ? <div className="crm-rollo__tl-text">{detail}</div> : null}
                  {entry.observacion ? <div className="crm-rollo__tl-obs">{`“${entry.observacion}”`}</div> : null}
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="crm-rollo__muted">Todavía no hay movimientos.</p>
      )}
    </>
  );
}

// ---- Closed gestión: what happened instead of the action panel.
export function ClosedSummary({ gestion }) {
  if (codeOf(gestion?.estado) === "CANCELADO") {
    return (
      <Alert tone="info" title={`Gestión cancelada el ${formatDate(gestion.fechaCierre)}`}>
        {gestion.motivoCancelacion ? `Motivo: ${labelFor(MOTIVO_CANCELACION_LABELS, gestion.motivoCancelacion)}` : null}
      </Alert>
    );
  }

  const entregada = gestion?.cantidadEntregada ?? gestion?.cantidadSolicitada;
  const pedido = gestion?.cantidadSolicitada;
  const detail = [
    `${formatRollos(entregada)}${pedido && Number(entregada) !== Number(pedido) ? ` (pedido: ${pedido})` : ""}`,
    gestion?.recibioNombre ? `Recibió ${gestion.recibioNombre}` : "",
    gestion?.cadete ? `Cadete ${gestion.cadete}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="crm-rollo__summary">
      <b>{`Entregada el ${formatDate(gestion?.fechaEntrega || gestion?.fechaCierre)}`}</b>
      <span>{detail}</span>
      <span className="crm-rollo__muted">Gestión cerrada. Está en Finalizadas.</span>
    </div>
  );
}

// ---- Two outcomes of En camino (design SegmentedControl as toggle buttons).
function OutcomeSwitch({ value, onChange }) {
  const options = [
    { value: ACCIONES.ENTREGAR, label: "Registrar entrega" },
    { value: ACCIONES.NO_ENTREGADO, label: "No se pudo entregar" },
  ];
  return (
    <div className="crm-rollo__seg" role="group" aria-label="Resultado del envío">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="crm-rollo__seg-btn"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function PrepararFields({ form, errors, onChange }) {
  return (
    <ObservacionField
      id={fieldId("observacion")}
      value={form.observacion}
      error={errors.observacion}
      placeholder="Contá cómo se está preparando el pedido"
      onChange={(value) => onChange("observacion", value)}
    />
  );
}

// S11: the cadete is free text; there is no list of cadetes to suggest from.
function EnviarFields({ form, errors, onChange }) {
  const input = (key, props) => ({
    id: fieldId(key),
    value: form[key],
    onChange: (event) => onChange(key, event.target.value),
    ...invalidProps(fieldId(key), errors[key]),
    ...props,
  });
  return (
    <>
      <Field id={fieldId("cadete")} label="Cadete" required full error={errors.cadete}>
        <input className="crm-rollo__input" type="text" autoComplete="off" placeholder="Nombre y apellido" {...input("cadete")} />
      </Field>
      <Field id={fieldId("fechaSalida")} label="Fecha de salida" required error={errors.fechaSalida}>
        <input className="crm-rollo__input" type="date" {...input("fechaSalida")} />
      </Field>
      <Field id={fieldId("horaSalida")} label="Hora de salida" required error={errors.horaSalida}>
        <input className="crm-rollo__input" type="time" {...input("horaSalida")} />
      </Field>
      <ObservacionField
        id={fieldId("observacion")}
        value={form.observacion}
        error={errors.observacion}
        placeholder="Algo que el cadete tenga que saber"
        onChange={(value) => onChange("observacion", value)}
      />
    </>
  );
}

function EntregarFields({ gestion, form, errors, today, onChange }) {
  const pedido = Number(gestion?.cantidadSolicitada) || 0;
  const cantidad = parseCantidad(form.cantidad);
  const differs = cantidad !== null && cantidad > 0 && pedido > 0 && cantidad !== pedido;
  const input = (key, props) => ({
    id: fieldId(key),
    value: form[key],
    onChange: (event) => onChange(key, event.target.value),
    ...invalidProps(fieldId(key), errors[key]),
    ...props,
  });
  return (
    <>
      <Field id={fieldId("fechaEntrega")} label="Fecha real de entrega" required error={errors.fechaEntrega}>
        <input className="crm-rollo__input" type="date" max={today || undefined} {...input("fechaEntrega")} />
      </Field>
      <Field
        id={fieldId("cantidad")}
        label="Cantidad entregada"
        required
        error={errors.cantidad}
        help={pedido ? `Pedido: ${formatRollos(pedido)}` : null}
      >
        <input
          className="crm-rollo__input"
          type="number"
          min={LIMITS.cantidadMin}
          max={LIMITS.cantidadMax}
          step="1"
          inputMode="numeric"
          {...input("cantidad")}
        />
      </Field>
      {differs ? (
        <div className="crm-rollo__fld--full">
          <Alert tone="warn" title={`Difiere de lo pedido (${pedido})`}>
            Si sabés por qué, anotalo en la observación.
          </Alert>
        </div>
      ) : null}
      <Field id={fieldId("recibio")} label="Quién recibió" required full error={errors.recibio}>
        <input className="crm-rollo__input" type="text" autoComplete="off" placeholder="Nombre y apellido" {...input("recibio")} />
      </Field>
      <ObservacionField
        id={fieldId("observacion")}
        value={form.observacion}
        error={errors.observacion}
        placeholder="Algo para recordar de esta entrega"
        onChange={(value) => onChange("observacion", value)}
      />
      <div className="crm-rollo__fld--full crm-rollo__help">Al confirmar, la gestión se cierra y pasa a Finalizadas.</div>
    </>
  );
}

function NoEntregadoFields({ form, errors, today, onChange }) {
  return (
    <>
      <MotivoField
        id={fieldId("motivo")}
        options={MOTIVO_NO_ENTREGA_OPTIONS}
        value={form.motivo}
        error={errors.motivo}
        onChange={(value) => onChange("motivo", value)}
      />
      <Field id={fieldId("nuevaFecha")} label="Nueva fecha estimada" optional error={errors.nuevaFecha}>
        <input
          className="crm-rollo__input"
          type="date"
          id={fieldId("nuevaFecha")}
          min={today || undefined}
          value={form.nuevaFecha}
          onChange={(event) => onChange("nuevaFecha", event.target.value)}
          {...invalidProps(fieldId("nuevaFecha"), errors.nuevaFecha)}
        />
      </Field>
      <ObservacionField
        id={fieldId("observacion")}
        value={form.observacion}
        error={errors.observacion}
        required={form.motivo === "OTRO"}
        placeholder="Contá qué pasó"
        onChange={(value) => onChange("observacion", value)}
      />
      <div className="crm-rollo__fld--full crm-rollo__help">Al confirmar, la gestión vuelve a En preparación.</div>
    </>
  );
}

const PANEL_COPY = {
  [ACCIONES.PREPARAR]: { title: "Pasar a En preparación", text: "Confirmá que los rollos se están preparando en depósito." },
  [ACCIONES.ENVIAR]: { title: "Enviar con cadete", text: "Al confirmar, la gestión pasa a En camino." },
};

/**
 * Action panel of the current estado (design RolloManagePrep "Acciones por estado"). The fieldset is disabled
 * while saving; En camino chooses between its two outcomes with a SegmentedControl.
 */
export function ActionPanel({ accion, gestion, form, errors, saving, today, onOutcomeChange, onChange }) {
  const enCamino = accion === ACCIONES.ENTREGAR || accion === ACCIONES.NO_ENTREGADO;
  const copy = enCamino
    ? {
        title: "¿Cómo terminó el envío?",
        text: gestion?.cadete
          ? `Salió con ${gestion.cadete}${gestion.fechaSalida ? ` el ${formatShortDate(gestion.fechaSalida)}` : ""}.`
          : null,
      }
    : PANEL_COPY[accion];

  const fields = {
    [ACCIONES.PREPARAR]: <PrepararFields form={form} errors={errors} onChange={onChange} />,
    [ACCIONES.ENVIAR]: <EnviarFields form={form} errors={errors} onChange={onChange} />,
    [ACCIONES.ENTREGAR]: <EntregarFields gestion={gestion} form={form} errors={errors} today={today} onChange={onChange} />,
    [ACCIONES.NO_ENTREGADO]: <NoEntregadoFields form={form} errors={errors} today={today} onChange={onChange} />,
  }[accion];

  return (
    <section className="crm-rollo__action" aria-labelledby="crm-rollo-gestionar-accion">
      <div>
        <h3 id="crm-rollo-gestionar-accion">{copy.title}</h3>
        {copy.text ? <p>{copy.text}</p> : null}
      </div>
      {enCamino ? <OutcomeSwitch value={accion} onChange={onOutcomeChange} /> : null}
      <fieldset className="crm-rollo__form" disabled={saving}>
        <legend className="crm-rollo__sr-only">{copy.title}</legend>
        {fields}
      </fieldset>
    </section>
  );
}

// ---- Body of the cancel confirmation (design RolloManageCancel).
export function CancelForm({ gestion, form, errors, saving, submitError, onChange }) {
  return (
    <>
      <p className="crm-rollo__confirm-text">
        La gestión se cierra y pasa a Finalizadas como <b>Cancelado</b>. No se puede deshacer.
      </p>
      {submitError ? (
        <Alert tone="error" role="alert" title={submitError.title}>
          {submitError.text}
        </Alert>
      ) : null}
      <fieldset className="crm-rollo__form crm-rollo__form--single" disabled={saving}>
        <legend className="crm-rollo__sr-only">{`Cancelar la gestión de ${gestion?.nombreComercio || "este comercio"}`}</legend>
        <MotivoField
          id={cancelFieldId("motivo")}
          options={MOTIVO_CANCELACION_OPTIONS}
          value={form.motivo}
          error={errors.motivo}
          onChange={(value) => onChange("motivo", value)}
        />
        <ObservacionField
          id={cancelFieldId("observacion")}
          value={form.observacion}
          error={errors.observacion}
          required={form.motivo === "OTRO"}
          placeholder="Contá por qué se cancela"
          onChange={(value) => onChange("observacion", value)}
        />
      </fieldset>
    </>
  );
}
